import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGS } from "@/i18n";
import { Globe, Check } from "@phosphor-icons/react";
import { useState, useRef, useEffect } from "react";

/**
 * Compact language picker. `variant` controls presentation:
 *   - "chip"    : inline pill (login, register, guest pages)
 *   - "header"  : minimal icon inside AppHeader
 */
export default function LanguageSelector({ variant = "chip", className = "" }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current =
    SUPPORTED_LANGS.find((l) => i18n.resolvedLanguage?.startsWith(l.code)) ||
    SUPPORTED_LANGS[0];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pick = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  const btnCls =
    variant === "header"
      ? "inline-flex items-center gap-1.5 text-neutral-400 hover:text-neutral-100 text-sm transition-colors"
      : "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-xs text-neutral-200 transition-colors";

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        data-testid="language-selector-btn"
        onClick={() => setOpen((v) => !v)}
        className={btnCls}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Globe size={14} weight="duotone" className="text-violet-300" />
        <span>{current.native}</span>
      </button>
      {open && (
        <div
          role="listbox"
          data-testid="language-selector-menu"
          className="absolute right-0 mt-2 min-w-[160px] rounded-xl border border-white/10 bg-neutral-950/95 backdrop-blur p-1 shadow-2xl z-50"
        >
          {SUPPORTED_LANGS.map((l) => {
            const active = l.code === current.code;
            return (
              <button
                key={l.code}
                data-testid={`language-option-${l.code}`}
                onClick={() => pick(l.code)}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  active
                    ? "bg-violet-500/15 text-violet-100"
                    : "text-neutral-200 hover:bg-white/[0.05]"
                }`}
              >
                <span>{l.native}</span>
                {active && <Check size={13} weight="bold" className="text-violet-300" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
